import { motion } from "framer-motion";
import { CHAPTERS, PRESS, FIELD, ARTISTS } from "../data/site";

const EASE = [0.16, 1, 0.3, 1];

export const About = () => (
  <section id="about" data-testid="about-section" className="bg-black px-4 md:px-10 py-20 md:py-28 border-t border-[#222]">
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease: EASE }}
      className="mb-12 md:mb-16"
    >
      <p className="font-jetmono text-[10px] tracking-[0.35em] uppercase text-[#808080] mb-4">03 / THE MANIFESTO</p>
      <h2 className="font-syncopate font-bold uppercase leading-none tracking-tight text-4xl sm:text-6xl lg:text-7xl">
        BEHIND<br />THE LENS<span className="text-outline-dim">.</span>
      </h2>
    </motion.div>

    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="lg:col-span-5"
      >
        <div className="relative aspect-[4/3] border border-[#222] overflow-hidden" data-media-slot="about-portrait">
          <img
            src="/media/khush-wide.webp"
            alt="Khush Patel — neon-lit portrait"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <span className="absolute bottom-4 left-4 font-jetmono text-[9px] tracking-[0.2em] uppercase text-white bg-black/60 px-2 py-1">
            KHUSH PATEL — LOS ANGELES
          </span>
        </div>
        <div className="mt-6 flex flex-wrap items-center gap-3 font-jetmono text-[10px] tracking-[0.2em] uppercase text-[#808080]">
          <span>FEATURED IN:</span>
          {PRESS.map((p) => (
            <a
              key={p.name}
              data-testid={`press-link-${p.name.toLowerCase().replace(/\s/g, "-")}`}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              className="border border-[#333] px-3 py-1.5 hover:bg-white hover:text-black hover:border-white transition-colors duration-300"
            >
              {p.name}
            </a>
          ))}
        </div>
      </motion.div>

      <div className="lg:col-span-7 flex flex-col">
        {CHAPTERS.map((c, i) => (
          <motion.div
            key={c.index}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: i * 0.1, ease: EASE }}
            className="border-t border-[#333] py-8 md:py-10 grid grid-cols-12 gap-4"
          >
            <span className="col-span-2 md:col-span-1 font-jetmono text-xs text-[#808080] pt-1">{c.index}</span>
            <div className="col-span-10 md:col-span-11">
              <h3 className="font-syncopate font-bold uppercase tracking-tight text-xl md:text-3xl mb-4">{c.title}</h3>
              <p className="font-plex font-light text-sm md:text-lg leading-relaxed text-[#B5B5B5] max-w-2xl">{c.body}</p>
            </div>
          </motion.div>
        ))}

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="border-t border-[#333] pt-8 grid grid-cols-3 gap-4 font-jetmono uppercase"
        >
          {[
            ["75+", "ARTISTS SHOT WORLDWIDE"],
            ["10+", "MAGAZINES PUBLISHED"],
            ["05", "COUNTRIES ON TOUR"],
          ].map(([stat, label]) => (
            <div key={label}>
              <p className="font-syncopate font-bold text-2xl md:text-4xl mb-2">{stat}</p>
              <p className="text-[9px] tracking-[0.15em] text-[#808080]">{label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </div>

    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, ease: EASE }}
      className="mt-16 md:mt-24"
      data-testid="field-strip"
    >
      <p className="font-jetmono text-[10px] tracking-[0.35em] uppercase text-[#808080] mb-6">
        IN THE FIELD — ON ASSIGNMENT
      </p>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px bg-[#222] border border-[#222]">
        {FIELD.map((f) => (
          <figure key={f.id} className="group relative bg-black overflow-hidden" data-testid={`field-img-${f.id}`}>
            <div className="aspect-square overflow-hidden">
              <img
                src={f.src}
                alt={f.caption}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
              />
            </div>
            <figcaption className="absolute bottom-0 left-0 right-0 px-3 py-2 font-jetmono text-[9px] tracking-[0.15em] uppercase text-white/90 bg-gradient-to-t from-black/80 to-transparent opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-500">
              {f.caption}
            </figcaption>
          </figure>
        ))}
      </div>
    </motion.div>

    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, ease: EASE }}
      className="mt-16 md:mt-24 border-t border-[#222] pt-10 md:pt-14"
      data-testid="artist-roster"
    >
      <p className="font-jetmono text-[10px] tracking-[0.35em] uppercase text-[#808080] mb-8">
        IN GOOD COMPANY — {ARTISTS.length} ARTISTS AND COUNTING
      </p>
      <p className="font-syncopate font-bold uppercase tracking-tight leading-[1.7] text-base sm:text-2xl md:text-3xl">
        {ARTISTS.map((artist, i) => (
          <span key={artist}>
            <span className="text-[#777] hover:text-white transition-colors duration-300 cursor-default">
              {artist}
            </span>
            {i < ARTISTS.length - 1 && <span className="text-[#2E2E2E]"> × </span>}
          </span>
        ))}
      </p>
    </motion.div>
  </section>
);
